import { useCallback, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import {
  InventoryDeeplinkQueryParam,
  PurchaseDeeplinkQueryParam,
  SaleDeeplinkQueryParam,
} from 'src/utils/constants/constants';
import {
  getDeepLinkIdFromUrl,
  isInFullPageEventView,
} from 'src/utils/deepLinkUtils';
import { splitOutTicketGroupId } from 'src/utils/purchaseUtils';
import { ListingDetailDataField } from 'src/WebApiController';

import { useListingActions } from './useListingActions';
import { usePurchaseActions } from './usePurchaseActions';
import { useSaleActions } from './useSaleActions';

export type DeeplinkType = 'purchase' | 'sale' | 'inventory' | null;

export interface MultipleDeeplinksResult {
  activeId: string;
  activeType: DeeplinkType;
  inventoryId: string;
  purchaseId: string;
  saleId: string;
  openActiveModal: (inventoryCondition?: () => boolean) => void;
}

/**
 * Hook that extracts multiple deeplink IDs from the current URL
 * and provides a function to open the corresponding modal
 * Following existing codebase patterns for deeplink handling
 *
 *
 * @returns {MultipleDeeplinksResult} Object containing activeId, activeType, individual IDs, and modal opener
 */
export const useMultipleDeeplinks = (): MultipleDeeplinksResult => {
  const { openListingModal } = useListingActions();
  const { openSaleModal } = useSaleActions();
  const { openPurchaseModal } = usePurchaseActions();
  const { mainRoute } = useAppContext();
  const isEventPage = isInFullPageEventView(mainRoute);

  const inventoryId = getDeepLinkIdFromUrl(
    InventoryDeeplinkQueryParam,
    window.location.href
  );

  const purchaseIdRaw = getDeepLinkIdFromUrl(
    PurchaseDeeplinkQueryParam,
    window.location.href
  );

  // Extract just the purchase ID from combined format like "9484531||{"ticketGroupId":13930543}"
  const { id } = splitOutTicketGroupId(purchaseIdRaw);
  const purchaseId = String(id);

  const saleId = getDeepLinkIdFromUrl(
    SaleDeeplinkQueryParam,
    window.location.href
  );

  const { activeId, activeType } = useMemo(() => {
    // TODO: https://thestubhub.atlassian.net/browse/POS-6928
    // Block deeplinks on event pages, we need to pass in ticketGroupContext and activeId separately into the deeplink hooks for it to work

    if (isEventPage) return { activeId: '', activeType: null };
    if (purchaseId)
      return { activeId: purchaseId, activeType: 'purchase' as const };
    if (saleId) return { activeId: saleId, activeType: 'sale' as const };
    if (inventoryId)
      return { activeId: inventoryId, activeType: 'inventory' as const };
    return { activeId: '', activeType: null };
  }, [isEventPage, purchaseId, saleId, inventoryId]);

  const openActiveModal = useCallback(() => {
    if (!activeId?.length) return;

    switch (activeType) {
      case 'purchase':
        openPurchaseModal(activeId);
        break;
      case 'sale':
        openSaleModal(activeId);
        break;
      case 'inventory':
        openListingModal(
          activeId,
          [ListingDetailDataField.Basic], // dataFields
          true, // forceRefresh
          false, // resetData
          true // sectional
        );
        break;
    }
  }, [
    activeId,
    activeType,
    openListingModal,
    openSaleModal,
    openPurchaseModal,
  ]);

  return {
    activeId, // Follows existing codebase naming convention
    activeType, // New: indicates which type of deeplink is active
    inventoryId, // Individual IDs available for specific use cases
    purchaseId,
    saleId,
    openActiveModal, // Function to open the active modal
  };
};

export default useMultipleDeeplinks;
